import React, { useEffect, useState } from "react";
import {
  FaBriefcase,
  FaCalendarAlt,
  FaEdit,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaStickyNote,
  FaUser,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import {
  PROFILE_ADDRESS,
  displayProfileValue,
} from "./customerProfileFields";

function DetailItem({ label, value, children }) {
  return (
    <div className="cportal-profile-view-item">
      <span className="cportal-profile-view-label">{label}</span>
      <span className="cportal-profile-view-value">{children ?? displayProfileValue(value)}</span>
    </div>
  );
}

function DetailSection({ icon: Icon, title, children, className = "" }) {
  return (
    <section className={`cportal-card cportal-detail-section ${className}`.trim()}>
      <h2 className="cportal-detail-section-title">
        {Icon ? <Icon className="cportal-detail-section-icon" aria-hidden="true" /> : null}
        {title}
      </h2>
      {children}
    </section>
  );
}

function initials(profile) {
  const first = (profile?.First_Name || "").trim().charAt(0);
  const last = (profile?.Last_Name || "").trim().charAt(0);
  return `${first}${last}`.toUpperCase() || "U";
}

function fullName(profile) {
  const name = (profile?.Full_Name || "").trim();
  if (name) return name;
  return `${(profile?.First_Name || "").trim()} ${(profile?.Last_Name || "").trim()}`.trim() || "User";
}

function phoneDisplay(profile) {
  const cc = profile?.Country_Code ? String(profile.Country_Code).trim() : "";
  const phone = profile?.Phone || profile?.Phone_Number || "";
  const digits = phone ? String(phone).trim() : "";
  if (!digits) return "—";
  return cc ? `${cc} ${digits}` : digits;
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return displayProfileValue(value);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "cportal-badge cportal-badge--success";
  if (s === "inactive" || s === "terminated" || s === "resigned") {
    return "cportal-badge cportal-badge--danger";
  }
  return "cportal-badge";
}

function CustomerProfileView() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get("/client/me");
        if (!active) return;
        setProfile(res.data?.data || {});
      } catch (_) {
        if (active) setError("Could not load your profile.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return <p className="cportal-muted">Loading…</p>;
  }

  if (error) {
    return <div className="cportal-alert cportal-alert-error">{error}</div>;
  }

  const isTenantUser = profile?.portal_role === "tenant_user";

  return (
    <>
      <div className="cportal-entity-hero">
        <div className="cportal-entity-hero-left">
          <div className="cportal-entity-avatar" aria-hidden="true">
            {initials(profile)}
          </div>
          <div className="cportal-entity-hero-meta">
            <h1 className="cportal-entity-hero-name">{fullName(profile)}</h1>
            <span className={statusBadgeClass(profile?.Status)}>
              {displayProfileValue(profile?.Status)}
            </span>
          </div>
        </div>
        <div className="cportal-entity-hero-actions">
          <button
            type="button"
            className="cportal-btn cportal-btn-ghost"
            onClick={() => navigate("/portal/profile/edit")}
          >
            <FaEdit aria-hidden="true" /> Edit
          </button>
        </div>
      </div>

      {isTenantUser ? (
        <>
          <div className="cportal-detail-grid">
            <DetailSection icon={FaUser} title="Contact information">
              <div className="cportal-profile-view-grid">
                <DetailItem label="First name" value={profile.First_Name} />
                <DetailItem label="Last name" value={profile.Last_Name} />
                <DetailItem label="Company email (login)" value={profile.Company_Email} />
                <DetailItem label="Personal email" value={profile.Email} />
                <DetailItem label="Phone">{phoneDisplay(profile)}</DetailItem>
                <DetailItem label="Alt phone" value={profile.Alt_Phone} />
              </div>
            </DetailSection>

            <DetailSection icon={FaBriefcase} title="Professional information">
              <div className="cportal-profile-view-grid">
                <DetailItem label="Employee ID" value={profile.Employee_Id} />
                <DetailItem label="Designation" value={profile.Designation} />
                <DetailItem label="Department" value={profile.Department} />
                <DetailItem label="Gender" value={profile.Gender} />
                <DetailItem label="Date of joining">{formatDate(profile.DOJ)}</DetailItem>
                <DetailItem label="Company" value={profile.Company_Name} />
              </div>
            </DetailSection>
          </div>

          <DetailSection icon={FaInfoCircle} title="Other details">
            <div className="cportal-profile-view-grid">
              <DetailItem label="Created on">
                <span className="cportal-datetime-value">
                  <FaCalendarAlt aria-hidden="true" />
                  {formatDateTime(profile.Created_On)}
                </span>
              </DetailItem>
              <DetailItem label="Updated on">
                <span className="cportal-datetime-value">
                  <FaCalendarAlt aria-hidden="true" />
                  {formatDateTime(profile.Updated_On)}
                </span>
              </DetailItem>
            </div>
          </DetailSection>

          {profile.Notes ? (
            <DetailSection icon={FaStickyNote} title="Notes">
              <div className="cportal-profile-view-grid">
                <DetailItem label="Notes" value={profile.Notes} />
              </div>
            </DetailSection>
          ) : null}
        </>
      ) : (
        <>
          <div className="cportal-detail-grid">
            <DetailSection icon={FaUser} title="Contact information">
              <div className="cportal-profile-view-grid">
                <DetailItem label="First name" value={profile.First_Name} />
                <DetailItem label="Last name" value={profile.Last_Name} />
                <DetailItem label="Email (login)" value={profile.Email} />
                <DetailItem label="Phone">{phoneDisplay(profile)}</DetailItem>
                <DetailItem label="Alternate phone" value={profile.Alt_Phone} />
              </div>
            </DetailSection>

            <DetailSection icon={FaMapMarkerAlt} title="Address">
              <div className="cportal-profile-view-grid">
                {PROFILE_ADDRESS.map(([key, label]) => (
                  <DetailItem key={key} label={label} value={profile?.[key]} />
                ))}
              </div>
            </DetailSection>
          </div>

          <DetailSection icon={FaInfoCircle} title="Other details">
            <div className="cportal-profile-view-grid">
              <DetailItem label="Company" value={profile.Company_Name} />
              <DetailItem label="Customer type" value={profile.Customer_Type} />
              <DetailItem label="Last login">
                <span className="cportal-datetime-value">
                  <FaCalendarAlt aria-hidden="true" />
                  {formatDateTime(profile.Last_Login)}
                </span>
              </DetailItem>
            </div>
          </DetailSection>
        </>
      )}
    </>
  );
}

export default CustomerProfileView;
